import { stripSourceMarkers } from '../../domain/assistant';
import { Fragment, type ReactNode } from 'react';

/**
 * Renders Elio's light markdown (paragraphs, bullet/numbered lists, **bold**, *italic*, links)
 * as React elements. No HTML from the model is ever injected.
 */
export function RichText({ text, className }: { text: string; className?: string }) {
  const blocks = stripSourceMarkers(text.replace(/\r/g, '')).split(/\n{2,}/);
  return (
    <div className={className}>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        if (lines.length >= 2 && lines.every((l) => l.trim().startsWith('|'))) {
          const rows = lines.filter((l) => !/^\s*\|?\s*:?-{2,}/.test(l)).map((l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim()));
          const [head, ...body] = rows;
          const numeric = (c: string) => /^[-–€$£¥]?\s?[\d.,]+(\s?[–-]\s?[€$£¥]?[\d.,]+)?\s?(%|[A-Z]{3})?$/.test(c.replace(/\*\*/g, ''));
          return (
            <div key={i} className="my-3 overflow-x-auto rounded-xl border border-line">
              <table className="w-full text-sm">
                <thead className="bg-ivory text-left"><tr>{head!.map((h, j) => <th key={j} className="px-3 py-2 font-semibold">{inline(h)}</th>)}</tr></thead>
                <tbody>{body.map((r, j) => <tr key={j} className="border-t border-line">{r.map((c, k) => <td key={k} className={'px-3 py-2 align-top' + (k > 0 && numeric(c) ? ' whitespace-nowrap text-right tabular-nums' : '')}>{inline(c)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        const isList = lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l) || !l.trim());
        if (isList) {
          const ordered = /^\s*\d+[.)]/.test(lines[0] ?? '');
          const items = lines.filter((l) => l.trim()).map((l) => l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
          const List = ordered ? 'ol' : 'ul';
          return <List key={i} className={ordered ? 'my-2 list-decimal space-y-1 pl-5' : 'my-2 list-disc space-y-1 pl-5'}>{items.map((it, j) => <li key={j}>{inline(it)}</li>)}</List>;
        }
        const heading = /^#{1,4}\s+(.*)$/.exec(lines[0] ?? '');
        if (heading && lines.length === 1) return <p key={i} className="mt-3 font-semibold first:mt-0">{inline(heading[1]!)}</p>;
        return <p key={i} className="my-2 first:mt-0 last:mb-0">{lines.map((l, j) => <Fragment key={j}>{j > 0 && <br />}{inline(l.replace(/^#{1,4}\s+/, ''))}</Fragment>)}</p>;
      })}
    </div>
  );
}

const TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]\((https?:\/\/[^)\s]+)\)|https?:\/\/[^\s)]+|\*[^*\s][^*]*\*)/g;

function inline(s: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const t = m[0];
    if (t.startsWith('**')) out.push(<strong key={m.index}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith('[')) {
      const label = t.slice(1, t.indexOf(']'));
      out.push(<a key={m.index} href={m[2]} target="_blank" rel="noopener noreferrer nofollow" className="font-medium text-turquoise-700 underline underline-offset-2">{label}</a>);
    } else if (t.startsWith('http')) out.push(<a key={m.index} href={t} target="_blank" rel="noopener noreferrer nofollow" className="break-all text-turquoise-700 underline underline-offset-2">{new URL(t).hostname}</a>);
    else out.push(<em key={m.index}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
